// src/components/conditions/components/CommentsRenderer.jsx
import {
  ChatBubbleOutline,
  ExpandLess,
  ExpandMore,
  Person,
  Send,
  SendOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { isEmpty, isFunction } from "lodash";
import { memo, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";

// ─── Main CommentsRenderer ───────────────────────────────────────────────────
const CommentsRenderer = memo(
  ({
    comments = [],
    currentUserCustomEmail,
    onAddComment,
    onCountClick,
    maxCollapsed = null,
    showReplyInput = true,
    showCount = true,
    onlyCount = false,
    onlyComments = false,
    onlyInput = false,
    hideIfEmpty = false,
    disabled = false,
    inputRows,
    loading,
    commentThreadTopMargin = 3,
    hideHeader = false,
    inputTypeBasic = false,
  }) => {
    const emailAddress = useSelector((state) => state?.user?.emailAddress);
    const currentUserEmail = currentUserCustomEmail || emailAddress || null;
    const [expanded, setExpanded] = useState(true);
    const commentCount = comments?.length || 0;
    const hasOverflow = commentCount > maxCollapsed;
    const visibleComments =
      expanded || !hasOverflow
        ? comments
        : comments.slice(commentCount - maxCollapsed);

    const handleToggle = useCallback(() => {
      setExpanded((prev) => !prev);
    }, []);

    const commentThreadContent = (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          // gap: 1.5,
          // mt: 1.5,
          // mb: 3,
        }}
      >
        {hideHeader ? null : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {showCount ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ChatBubbleOutline sx={{ fontSize: 13, color: "#8a8577" }} />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#8a8577",
                    fontSize: "12px",
                    fontWeight: 600,
                    letterSpacing: "0.8px",
                  }}
                >
                  {commentCount === 1 ? "COMMENTS" : "COMMENTS"} ({commentCount}
                  )
                </Typography>
              </Box>
            ) : null}

            {hasOverflow && (
              <Chip
                size="small"
                variant="outlined"
                label={expanded ? "Show less" : `Show all ${commentCount}`}
                icon={
                  expanded ? (
                    <ExpandLess sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 16 }} />
                  )
                }
                onClick={handleToggle}
                sx={{ height: 24, fontSize: "0.7rem" }}
              />
            )}
          </Box>
        )}
        {isEmpty(visibleComments) ? null : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              mt: commentThreadTopMargin,
              // mb: 3,
            }}
          >
            {visibleComments.map((comment, index) => (
              <CommentMessage
                key={comment._id || comment.commentDateTime || index}
                comment={comment}
                currentUserEmail={currentUserEmail}
              />
            ))}
          </Box>
        )}
      </Box>
    );

    const commentInputContent = !isFunction(onAddComment) ? null : (
      <Box sx={{ mt: 0 }}>
        <CommentReplyInput
          onSubmit={onAddComment}
          disabled={disabled}
          loading={loading}
          basicInput={inputTypeBasic}
          rows={inputRows}
        />
      </Box>
    );

    // ── Count-only mode (great for AG Grid cells, table rows, etc.) ──
    if (onlyCount) {
      return <CommentCountBadge count={commentCount} onClick={onCountClick} />;
    }

    if (onlyComments) {
      return commentThreadContent;
    }

    if (onlyInput) {
      return commentInputContent;
    }

    if (commentCount === 0 && !showReplyInput) return null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {/* Header */}

        {/* Comment Thread */}
        {commentThreadContent}

        {/* Reply Input */}
        {commentInputContent}
      </Box>
    );
  },
);

// ─── Comment Bubble (mirrors MessageBubble pattern) ──────────────────────────
const CommentBubble = memo(({ isCurrentUser, children }) => {
  const getBgColor = () => (isCurrentUser ? blue[50] : "white");
  const getBorderColor = () => (isCurrentUser ? blue[100] : grey[300]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        bgcolor: getBgColor(),
        border: `1px solid ${getBorderColor()}`,
        borderRadius: "12px",
        borderTopLeftRadius: isCurrentUser ? "12px" : "4px",
        borderTopRightRadius: isCurrentUser ? "4px" : "12px",
        width: "auto",
        minWidth: 140,
      }}
    >
      {children}
    </Paper>
  );
});

CommentBubble.displayName = "CommentBubble";

// ─── Single Comment (mirrors ChatMessage pattern) ────────────────────────────
const CommentMessage = memo(({ comment, currentUserEmail }) => {
  const { htmlString, displayName, submittedBy, commentDateTime } = comment;

  const isCurrentUser =
    currentUserEmail && submittedBy && submittedBy === currentUserEmail;

  const plainText = htmlString
    ? htmlString.replace(/<[^>]*>/g, "")
    : comment.content || "";

  // After:
  const timeAgo = commentDateTime ? formatDateTime(commentDateTime) : null;

  const fullTimestamp = commentDateTime
    ? formatDateTime(commentDateTime, "MMM d, yyyy h:mm a")
    : null;
  const initials = displayName
    ? displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isCurrentUser ? "flex-end" : "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          maxWidth: "85%",
          flexDirection: isCurrentUser ? "row-reverse" : "row",
          alignItems: "flex-start",
        }}
      >
        {/* Avatar */}
        <Tooltip title={displayName || submittedBy || "Unknown"}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: isCurrentUser ? "primary.main" : grey[400],
              fontSize: "0.7rem",
            }}
            variant="rounded"
          >
            {initials || <Person sx={{ fontSize: 16 }} />}
          </Avatar>
        </Tooltip>

        {/* Bubble */}
        <CommentBubble isCurrentUser={isCurrentUser}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {displayName || submittedBy || "Unknown User"}
            </Typography>
            {timeAgo && (
              <Tooltip title={fullTimestamp} placement="top">
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                >
                  {timeAgo}
                </Typography>
              </Tooltip>
            )}
          </Box>

          {/* Body */}
          {htmlString ? (
            <Typography
              variant="body2"
              sx={{
                wordBreak: "break-word",
                lineHeight: 1.5,
                "& p": { m: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: htmlString }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.5,
              }}
            >
              {plainText || "..."}
            </Typography>
          )}
        </CommentBubble>
      </Box>
    </Box>
  );
});

CommentMessage.displayName = "CommentMessage";

// ─── Reply Input ─────────────────────────────────────────────────────────────
const CommentReplyInput = memo(
  ({ onSubmit, rows = 1, disabled, loading, basicInput = false }) => {
    const [value, setValue] = useState("");

    const handleSubmit = useCallback(() => {
      const trimmed = value.trim();
      if (!trimmed) return;
      onSubmit(trimmed);
      setValue("");
    }, [value, onSubmit]);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSubmit();
        }
      },
      [handleSubmit],
    );

    if (basicInput) {
      return (
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          rows={rows}
          placeholder="Add a comment..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  // loading={loading}
                  size="small"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!value.trim() || disabled || loading}
                >
                  {loading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <Send sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              fontSize: "0.875rem",
            },
          }}
        />
      );
    }
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1,
          pl: 0.5,
          borderRadius: 0,
          borderTop: "1px solid rgb(224, 221, 213)",
          // borderTop: `1px solid ${grey[200]}`,
        }}
      >
        <Box sx={{ display: "flex", gap: 0, alignItems: "center" }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            sx={{
              ".MuiInputBase-root": {
                p: 2,
              },
              fieldset: {
                border: "none",
              },
            }}
            placeholder="Add a comment..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            // onKeyPress={handleKeyPress}
            onKeyDown={handleKeyDown}
            disabled={disabled || loading}
          />
          <Button
            loading={loading}
            variant="contained"
            onClick={handleSubmit}
            disabled={disabled || loading || !value.trim()}
            sx={{ minWidth: 48, height: 48 }}
          >
            <SendOutlined />
          </Button>
        </Box>
      </Paper>
    );
  },
);

CommentReplyInput.displayName = "CommentReplyInput";

// ─── Count-Only Badge ────────────────────────────────────────────────────────
const CommentCountBadge = memo(({ count, onClick }) => {
  return (
    <Tooltip
      title={
        isFunction(onClick)
          ? `${count} ${count === 1 ? "comment" : "comments"} — click to expand`
          : undefined
      }
    >
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: onClick ? "pointer" : "default",
          color: count > 0 ? "primary.main" : "text.secondary",
          "&:hover": onClick ? { color: "primary.dark" } : undefined,
        }}
      >
        <ChatBubbleOutline sx={{ fontSize: 14 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
});

CommentCountBadge.displayName = "CommentCountBadge";

CommentsRenderer.displayName = "CommentsRenderer";

export default CommentsRenderer;
